import { Card, ListaControlesSkeleton, RegionCargando, Skeleton } from "@/components/ui";

/**
 * Lista de tarjetas-fila en carga (configuraciones / predefinidos): nombre con
 * badge opcional, 1-2 líneas de detalle y los botones de ícono a la derecha.
 */
export function FilasTarjetaSkeleton({
  filas = 4,
  botones = 2,
  conBadge = false,
  lineas = 1,
  etiqueta,
}: {
  filas?: number;
  botones?: number;
  conBadge?: boolean;
  lineas?: 1 | 2;
  etiqueta?: string;
}) {
  return (
    <RegionCargando etiqueta={etiqueta} className="space-y-2">
      {Array.from({ length: filas }, (_, i) => (
        <Card key={i} className="flex items-center gap-3 p-3" aria-hidden="true">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Skeleton height={14} width={i % 2 === 0 ? 200 : 160} className="max-w-full" />
              {conBadge && <Skeleton height={16} width={64} className="rounded-full" />}
            </div>
            <Skeleton height={10} width={i % 2 === 0 ? "55%" : "45%"} />
            {lineas === 2 && <Skeleton height={10} width="35%" />}
          </div>
          {Array.from({ length: botones }, (_, b) => (
            <Skeleton key={b} height={30} width={30} className="shrink-0" />
          ))}
        </Card>
      ))}
    </RegionCargando>
  );
}

/** Editor de configuración en carga · barra (volver + título + Guardar) y las tarjetas del formulario. */
export function ConfiguracionEditorSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando configuración…" className="flex flex-col gap-4">
      <div className="flex items-center justify-between" aria-hidden="true">
        <div className="flex items-center gap-2">
          <Skeleton height={30} width={30} />
          <Skeleton height={20} width={220} />
        </div>
        <Skeleton height={36} width={110} />
      </div>
      <Card className="flex flex-col gap-4 p-4" aria-hidden="true">
        {[36, 60].map((alto, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton height={12} width={i === 0 ? 60 : 140} />
            <Skeleton height={alto} />
          </div>
        ))}
        <div className="flex items-end gap-6">
          <div className="flex w-52 flex-col gap-1.5">
            <Skeleton height={12} width={120} />
            <Skeleton height={36} />
          </div>
          <Skeleton height={20} width={80} className="mb-2 rounded-full" />
        </div>
      </Card>
      <Card className="p-4">
        <Skeleton height={16} width={180} aria-hidden="true" />
        <ListaControlesSkeleton filas={4} control="checkbox" conBorde className="mt-3" anunciar={false} />
      </Card>
    </RegionCargando>
  );
}

/** Builder de predefinidos en carga · barra superior y las 3 columnas (tablas · columnas/ajustes · vista previa). */
export function BuilderSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando el constructor de reportes…" className="flex h-[calc(100dvh-4rem)] flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2" aria-hidden="true">
        <Skeleton height={32} width={140} />
        <div className="flex gap-2">
          <Skeleton height={32} width={220} />
          <Skeleton height={32} width={110} />
          <Skeleton height={32} width={130} />
        </div>
      </div>
      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)_minmax(0,1fr)]" aria-hidden="true">
        <Card className="flex flex-col gap-2.5 p-3">
          <Skeleton height={32} />
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} height={12} width={i % 2 === 0 ? "70%" : "55%"} />
          ))}
        </Card>
        <Card className="flex flex-col gap-3 p-3">
          <Skeleton height={14} width="40%" />
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={32} />
          ))}
        </Card>
        <Card className="flex flex-col gap-2 p-3">
          <Skeleton height={14} width="30%" />
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height={12} />
          ))}
        </Card>
      </div>
    </RegionCargando>
  );
}
